/**
 * THE BACK OFFICE'S ONLY WAY IN.
 *
 * One file rather than one per endpoint family, which is a deliberate departure
 * from the till. The POS splits its clients because each is a separately
 * degradable path with its own offline story — a menu it can serve from cache,
 * a lease it can spend without a network, an outbox it must never lose. The
 * console has exactly one story: it asks, and if the answer does not come it
 * says so. Splitting that into eight files would be ceremony.
 *
 * What it keeps from the till is the shape that matters: an args object, an
 * INJECTED `fetch` so parsing is testable without a server, a named error
 * rather than an empty result, and MONEY PARSED BACK TO BIGINT at the boundary.
 * A decimal string that reaches a component is a float waiting to happen.
 */

import type { Translations } from "@gf/contracts/values";
import type { PrintDoc } from "@gf/module-printing";
import type { PlannedItem } from "./menu-import";

export class ApiError extends Error {
  readonly status: number;
  /** The problem document's slug, when the server sent one. */
  readonly kind: string;

  constructor(status: number, kind: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.kind = kind;
  }
}

export interface ApiArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly fetch?: typeof fetch;
}

// --- Wire shapes -----------------------------------------------------------

interface WireTaxLine {
  rateBp: number;
  grossMinor: string;
  vatMinor: string;
  netMinor: string;
}

interface WireTenderLine {
  kind: string;
  amountMinor: string;
  tipMinor: string;
  roundingMinor: string;
  count: number;
}

interface WireDay {
  businessDate: string;
  cashPointId: string | null;
  checkCount: number;
  refundCount?: number;
  grossMinor: string;
  discountMinor: string;
  compMinor: string;
  dueMinor: string;
  vatMinor: string;
  netMinor: string;
  tipMinor: string;
  roundingMinor: string;
  averageCheckMinor: string;
  /** Absent from a server older than covers. */
  covers?: number;
  perCoverMinor?: string;
  taxLines: WireTaxLine[];
  tenders: WireTenderLine[];
}

// --- Parsed shapes ---------------------------------------------------------

export interface TaxLine {
  readonly rateBp: number;
  readonly grossMinor: bigint;
  readonly vatMinor: bigint;
  readonly netMinor: bigint;
}

export interface TenderLine {
  readonly kind: string;
  readonly amountMinor: bigint;
  readonly tipMinor: bigint;
  readonly roundingMinor: bigint;
  readonly count: number;
}

export interface VenueIdentity {
  readonly legalName: string | null;
  readonly registrationId: string | null;
  readonly vatId: string | null;
  readonly legalAddress: string | null;
  readonly fiscalRegistrationId: string | null;
  readonly fiscalUnitId: number | null;
}

export interface VenueTable {
  readonly tableRef: string;
  readonly name: string;
  readonly seats: number;
  readonly area: string;
  readonly active: boolean;
}

/** Where one payment form is booked in the accountant's Pohoda. */
export interface PohodaBooking {
  readonly cashAccount: string;
  readonly accounting: string | null;
}

export interface PohodaSettings {
  readonly tenders: Readonly<Record<string, PohodaBooking>>;
}

/** `GET /v1/venue/modules`: what the plan sells, what the owner chose, what is in effect. */
export interface VenueModules {
  readonly catalogue: readonly { key: string; tier: "core" | "service" | "optional" | "preview"; requires: readonly string[] }[];
  readonly presets: readonly { id: string; modules: readonly string[] }[];
  readonly plan: { planId: string | null; validUntil: string | null; lapsed: boolean; modules: readonly string[] };
  readonly selection: readonly string[];
  readonly explicit: boolean;
  readonly effective: readonly string[];
}

export interface VenueSettings {
  readonly identity: VenueIdentity;
  readonly jurisdiction: string;
  /** Whether guests may order from their phones (the bill reads either way). */
  readonly guestOrdering?: boolean;
  /** Null until somebody sets which Pokladna each payment form goes into. */
  readonly bookkeeping?: { readonly pohoda: PohodaSettings | null };
  readonly floor: readonly VenueTable[];
  readonly published: {
    readonly version: number;
    readonly issuedAt: string;
    /** The tills have the floor and the company as saved; false means a publish is due. */
    readonly current?: boolean;
  } | null;
}

export interface StaffRow {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string;
  /** False for somebody who has left: kept, marked, off every till. */
  readonly active?: boolean;
}

export interface SalesDay {
  readonly businessDate: string;
  readonly cashPointId: string | null;
  readonly checkCount: number;
  /** Receipts taken back that day; their amounts are already netted in. */
  readonly refundCount: number;
  readonly grossMinor: bigint;
  readonly discountMinor: bigint;
  readonly compMinor: bigint;
  readonly dueMinor: bigint;
  readonly vatMinor: bigint;
  readonly netMinor: bigint;
  readonly tipMinor: bigint;
  readonly roundingMinor: bigint;
  readonly averageCheckMinor: bigint;
  /** Guests at the tables, where the till knew how many; the counter is not counted. */
  readonly covers: number;
  /** What those tables owed per guest. */
  readonly perCoverMinor: bigint;
  readonly taxLines: readonly TaxLine[];
  readonly tenders: readonly TenderLine[];
}

export interface SalesReport {
  readonly from: string;
  readonly to: string;
  readonly days: readonly SalesDay[];
  readonly totals: SalesDay;
  readonly gaps: readonly { readonly businessDate: string; readonly reason: string }[];
}

export interface Breakdown {
  readonly categories: readonly { readonly categoryId: string; readonly name: string }[];
  readonly items: readonly {
    readonly itemRef: string;
    readonly name: string;
    readonly categoryId: string | null;
    readonly quantityMilli: number;
    readonly grossMinor: bigint;
    readonly discountMinor: bigint;
    readonly compMinor: bigint;
    readonly chargedMinor: bigint;
  }[];
  readonly staff: readonly {
    readonly staffId: string;
    readonly checkCount: number;
    readonly salesMinor: bigint;
    readonly averageMinor: bigint;
    readonly tipMinor: bigint;
    readonly discountMinor: bigint;
    readonly compMinor: bigint;
    readonly voidCount: number;
    readonly voidGrossMinor: bigint;
  }[];
  readonly hours: readonly { readonly hour: number; readonly checkCount: number; readonly salesMinor: bigint }[];
}

export interface CheckSummary {
  readonly orderId: string;
  readonly checkId: string;
  readonly tableRef: string | null;
  /** What the floor calls it; absent from an older server. */
  readonly tableName?: string | null;
  readonly businessDate: string;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly state: "OPEN" | "SETTLED";
  readonly staffId: string;
  readonly dueMinor: bigint;
  readonly vatMinor: bigint;
  readonly tipMinor: bigint;
  readonly outstandingMinor: bigint;
  readonly receiptNumber: string | null;
  readonly tenderKinds: readonly string[];
}

export interface CheckDetail {
  readonly orderId: string;
  readonly businessDate: string;
  readonly checks: readonly {
    readonly checkId: string;
    readonly closedAt: string | null;
    readonly dueMinor: bigint;
    readonly vatMinor: bigint;
    readonly netMinor: bigint;
    readonly tipMinor: bigint;
    readonly roundingMinor: bigint;
    readonly taxLines: readonly TaxLine[];
    readonly receiptNumber: string | null;
    /**
     * `null` means NOTHING WAS EVER QUEUED for this sale — a worse answer than
     * "queued and waiting", and a different one, so it is a different value.
     */
    readonly fiscal: CheckFiscalStatus | null;
  }[];
  readonly lines: readonly {
    readonly selectionId: string;
    readonly name: string;
    readonly quantityMilli: number;
    readonly grossMinor: bigint;
    readonly rateBp: number;
    readonly firedAt: string | null;
    readonly voided: boolean;
    readonly voidReason: string | null;
  }[];
  readonly tenders: readonly {
    readonly tenderId: string;
    readonly kind: string;
    readonly amountMinor: bigint;
    readonly tipMinor: bigint;
    readonly roundingMinor: bigint;
    readonly voided: boolean;
  }[];
  readonly anomalies: readonly { readonly kind: string; readonly detail: string }[];
}

/**
 * Cash the shift report could not place, and the shifts it did.
 *
 * The unattributed total is a number that must never be quietly zero. A cash
 * tender rung with no drawer open — or while two were on one device — appears
 * in NO Z-report, so without this line it is money that simply is not on any
 * screen, and nobody goes looking for cash they were never told about.
 */
export interface ShiftPage {
  readonly shifts: readonly ShiftReport[];
  readonly unattributed: {
    readonly cashMinor: bigint;
    readonly tenders: number;
    readonly ambiguous: number;
  };
}

export interface ShiftReport {
  readonly shiftId: string;
  readonly businessDate: string;
  readonly cashierId: string;
  readonly cashierName: string | null;
  readonly drawerId: string;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly state: "OPEN" | "CLOSED";
  readonly expectedMinor: bigint;
  readonly countedMinor: bigint | null;
  readonly breakdown: Readonly<Record<string, bigint>>;
  readonly variance: {
    readonly varianceMinor: bigint;
    readonly direction: "OVER" | "SHORT" | "EXACT";
    readonly severity: "OK" | "WATCH" | "ALERT";
    readonly relativeBp: number | null;
  } | null;
  readonly movements: readonly {
    readonly kind: string;
    readonly amountMinor: bigint;
    readonly reasonCode: string;
  }[];
  readonly anomalies: readonly { readonly kind: string; readonly detail: string }[];
}

export interface KitchenTimings {
  readonly ticketCount: number;
  readonly servedCount: number;
  readonly medianServiceSeconds: number | null;
  readonly p90ServiceSeconds: number | null;
  readonly byStation: readonly {
    readonly stationId: string;
    /** What the venue calls it. Null for a station since deleted. */
    readonly stationName?: string | null;
    readonly ticketCount: number;
    readonly medianServiceSeconds: number | null;
  }[];
  readonly late: readonly {
    readonly ticketId: string;
    readonly stationId: string;
    readonly stationName?: string | null;
    readonly tableRef: string;
    readonly tableName?: string | null;
    readonly firedAt: string;
    readonly serviceSeconds: number | null;
  }[];
}

/** A check's standing with the Financial Administration. */
export interface CheckFiscalStatus {
  readonly state: string;
  /** A Playground acknowledgement discharges nothing. Never a green tick. */
  readonly test: boolean;
  readonly pok: string | null;
  readonly code: number | null;
  readonly message: string | null;
  readonly attempts: number;
  readonly warnings: readonly { readonly code: number; readonly message: string }[];
}

export interface FiscalState {
  readonly credentials: "present" | "absent";
  readonly oldestPendingSaleAt: string | null;
  readonly quarantined: readonly Record<string, unknown>[];
  /**
   * Reported, and reporting NOTHING.
   *
   * A Playground acknowledgement discharges no obligation, so it lands here
   * rather than in a reported column. It is the one failure that looks exactly
   * like success, and the demo shows it on purpose.
   */
  readonly playground: readonly Record<string, unknown>[];
  readonly unreportable: readonly Record<string, unknown>[];
}

export interface MenuItem {
  readonly itemRef: string;
  readonly name: string;
  readonly stationId: string;
  readonly allergens: readonly string[];
  readonly byMode: Readonly<
    Record<
      string,
      { readonly grossMinor: bigint; readonly vatMinor: bigint; readonly rateBp: number }
    >
  >;
}

export interface Menu {
  readonly version: number;
  readonly resolvedAt: string;
  readonly items: readonly MenuItem[];
}

/** The draft a manager edits: everything, including what is switched off. */
export interface DraftOption {
  readonly optionId: string;
  readonly name: string;
  readonly priceDeltaMinor: bigint;
  readonly maxQuantity: number;
  readonly active: boolean;
  readonly translations?: Translations;
}

export interface DraftGroup {
  readonly groupId: string;
  readonly name: string;
  readonly minSelections: number;
  readonly maxSelections: number | null;
  readonly sortOrder: number;
  readonly active: boolean;
  readonly options: readonly DraftOption[];
  readonly translations?: Translations;
}

export interface DraftItem {
  readonly itemRef: string;
  readonly name: string;
  readonly categoryId: string | null;
  readonly stationId: string;
  readonly taxCategory: string;
  readonly grossMinor: bigint;
  readonly sortOrder: number;
  readonly active: boolean;
  readonly allergens: readonly string[];
  readonly ageRestricted: boolean;
  readonly description: string | null;
  readonly modifierGroupIds: readonly string[];
  /** What a guest sees of it online. Absent on a server older than the field. */
  readonly photoSha?: string | null;
  readonly translations?: Translations;
  readonly dietary?: readonly string[];
  readonly featured?: boolean;
}

export interface DraftMenu {
  /** Edited since the last publish — the tills are not selling it yet. */
  readonly unpublishedChanges: boolean;
  readonly categories: readonly {
    readonly categoryId: string;
    readonly name: string;
    readonly sortOrder: number;
    readonly active: boolean;
  }[];
  readonly stations: readonly { readonly stationId: string; readonly name: string }[];
  readonly modifierGroups: readonly DraftGroup[];
  readonly items: readonly DraftItem[];
}

/** An item as the editor submits it. Money crosses as a decimal string. */
export interface ItemInput {
  readonly name?: string;
  readonly categoryId?: string | null;
  readonly stationId?: string;
  readonly taxCategory?: string;
  readonly grossMinor?: bigint;
  readonly sortOrder?: number;
  readonly active?: boolean;
  readonly allergens?: readonly string[];
  readonly ageRestricted?: boolean;
  readonly description?: string | null;
  readonly modifierGroupIds?: readonly string[];
  readonly photoSha?: string | null;
  readonly translations?: Translations;
  readonly dietary?: readonly string[];
  readonly featured?: boolean;
}

export interface GroupInput {
  readonly name: string;
  readonly minSelections: number;
  readonly maxSelections: number | null;
  readonly sortOrder?: number;
  readonly active?: boolean;
  readonly options: readonly {
    readonly optionId?: string;
    readonly name: string;
    readonly priceDeltaMinor: bigint;
    readonly maxQuantity?: number;
    readonly translations?: Translations;
  }[];
  readonly translations?: Translations;
}

const itemWire = (input: ItemInput): Record<string, unknown> => {
  const { grossMinor, ...rest } = input;
  return { ...rest, ...(grossMinor !== undefined ? { grossMinor: grossMinor.toString() } : {}) };
};

/** One printable card: a table, its code, and the URL a phone opens. */
export interface TableCard {
  readonly tableRef: string;
  readonly code: string;
  /** Null when the deployment has not been told where the guest app lives. */
  readonly url: string | null;
}

export interface TableCards {
  readonly venueName: string;
  /** When every card in this batch stops working. Said in words on the screen. */
  readonly expiresAt: string;
  readonly sessionMode: "CHECK" | "TABLE";
  readonly cards: readonly TableCard[];
}

// --- The client ------------------------------------------------------------

const big = (value: unknown): bigint => BigInt(String(value ?? "0"));

const taxLines = (lines: readonly WireTaxLine[] = []): TaxLine[] =>
  lines.map((l) => ({
    rateBp: l.rateBp,
    grossMinor: big(l.grossMinor),
    vatMinor: big(l.vatMinor),
    netMinor: big(l.netMinor),
  }));

const day = (d: WireDay): SalesDay => ({
  businessDate: d.businessDate,
  cashPointId: d.cashPointId,
  checkCount: d.checkCount,
  refundCount: d.refundCount ?? 0,
  grossMinor: big(d.grossMinor),
  discountMinor: big(d.discountMinor),
  compMinor: big(d.compMinor),
  dueMinor: big(d.dueMinor),
  vatMinor: big(d.vatMinor),
  netMinor: big(d.netMinor),
  tipMinor: big(d.tipMinor),
  roundingMinor: big(d.roundingMinor),
  averageCheckMinor: big(d.averageCheckMinor),
  covers: d.covers ?? 0,
  perCoverMinor: big(d.perCoverMinor ?? "0"),
  taxLines: taxLines(d.taxLines),
  tenders: (d.tenders ?? []).map((t) => ({
    kind: t.kind,
    amountMinor: big(t.amountMinor),
    tipMinor: big(t.tipMinor),
    roundingMinor: big(t.roundingMinor),
    count: t.count,
  })),
});


// --- Stock -------------------------------------------------------------------------

/** The ledger's base units. The office shows them as kg, l and ks. */
export type StockUnit = "g" | "ml" | "ea";

export interface StockItemRow {
  readonly itemRef: string;
  readonly name: string;
  readonly unit: StockUnit;
  readonly category: string;
  readonly active: boolean;
  /** Thousandths of the base unit. */
  readonly reorderMilli: bigint | null;
  readonly standardCostMilliMinor: bigint | null;
  readonly onHandMilli: bigint;
  readonly valueMinor: bigint;
  /** Milli-minor per base unit: the average held, else the last paid, else the standard. */
  readonly unitCostMilliMinor: bigint | null;
  readonly low: boolean;
}

export interface StockItemInput {
  readonly name: string;
  readonly unit: StockUnit;
  readonly category: string;
  readonly reorderMilli: bigint | null;
  readonly standardCostMilliMinor: bigint | null;
  readonly active: boolean;
}

export interface SupplierRow {
  readonly supplierRef: string;
  readonly name: string;
  readonly registrationId: string | null;
}

export interface RecipeLineRow {
  readonly componentRef: string;
  readonly quantityMilli: bigint;
  readonly unit: string;
  readonly yieldPctBp?: number;
}

export interface RecipeRow {
  readonly recipeRef: string;
  readonly version: number;
  readonly effectiveFrom: string;
  readonly yieldMilli: bigint;
  readonly yieldUnit: string;
  readonly lines: readonly RecipeLineRow[];
  /** One portion at today's prices; null when the recipe cannot be exploded. */
  readonly costMinor: bigint | null;
  /** Ingredients with no price yet, so the cost is understated. */
  readonly uncosted: readonly string[];
}

export interface OptionDeltaRow {
  readonly groupId: string;
  readonly optionId: string;
  readonly version: number;
  readonly lines: readonly { itemRef: string; quantityMilli: bigint }[];
}

export interface StockMovementRow {
  readonly entryId: string;
  readonly itemRef: string;
  readonly kind: string;
  readonly quantityMilli: bigint;
  readonly valueMinor: bigint;
  readonly occurredAt: string;
  readonly sourceRef: string;
  readonly reasonCode: string | null;
}

export interface FoodCostRow {
  readonly itemRef: string;
  readonly soldMilli: bigint;
  readonly netMinor: bigint;
  readonly costMinor: bigint;
}

export interface StockLine {
  readonly itemRef: string;
  /** Thousandths of `unit`, which may be kg or l as well as the base unit. */
  readonly quantityMilli: bigint;
  readonly unit: string;
}

export interface CountPreviewLine {
  readonly itemRef: string;
  readonly expectedMilli: bigint;
  readonly countedMilli: bigint;
  readonly varianceMilli: bigint;
  readonly varianceMinor: bigint | null;
}

/** An approval the server judged and flagged (`approval_check`). */
export interface ApprovalFlagRow {
  readonly eventId: string;
  readonly orderId: string;
  readonly act: "VOID_SENT" | "DISCOUNT" | "COMP" | "REFUND";
  readonly authorizedBy: string;
  readonly approverName: string | null;
  readonly actorName: string | null;
  readonly permission: string;
  readonly problem: "NOT_PERMITTED" | "UNKNOWN_APPROVER" | "INACTIVE_APPROVER";
  readonly occurredAt: string;
}

/** A venue's public page, as the office edits it (`/v1/venue/public`). */
export interface PublicPage {
  readonly slug: string | null;
  /** Where a guest finds it, when it has an address and the server knows the guest app's origin. */
  readonly url: string | null;
  readonly profile: {
    readonly tagline?: string;
    readonly address?: string;
    readonly phone?: string;
    readonly hours: Readonly<Record<string, readonly { open: string; close: string }[]>>;
    readonly online: { readonly paused: boolean; readonly prepMinutes: number; readonly slotMinutes: number };
    readonly bookings: BookingRules;
  };
}

/** One change in the office's history (`/v1/audit`), its references named where the venue has them. */
export interface AuditRow {
  readonly sequence: number;
  readonly at: string;
  readonly action: string;
  readonly actor: { readonly id: string; readonly name: string | null };
  readonly subject: string | null;
  readonly outcome: string;
  /** References, codes, counts, flags — and amounts as decimal strings. */
  readonly body: Readonly<Record<string, string | number | boolean | null>>;
}

export interface AuditLog {
  readonly chain: { readonly intact: boolean; readonly checked: number; readonly brokenAt?: number; readonly reason?: string };
  readonly entries: readonly AuditRow[];
}

/** A printer, as the office sets it up (`/v1/print/printers`). */
export interface PrinterRow {
  readonly printerId: string;
  readonly name: string;
  readonly transport: "SCREEN" | "HUB" | "CLOUDPRNT";
  readonly columns: number;
  readonly stations: readonly string[];
  readonly receipts: boolean;
  readonly active: boolean;
  /** Since when it has printed; tickets fired before are not its business. */
  readonly activeSince?: string;
}

export interface Printers {
  readonly printers: readonly PrinterRow[];
  readonly options: { readonly transports: readonly PrinterRow["transport"][]; readonly columns: readonly number[]; readonly staleAfterMinutes: number };
}

/** One piece of paper on a roll — or one that never made it, and why. */
export interface PrintJobRow {
  readonly jobId: string;
  readonly printerId: string | null;
  readonly kind: "KITCHEN" | "RECEIPT";
  readonly status: "QUEUED" | "PRINTED" | "STALE" | "UNROUTED";
  readonly label: string;
  readonly doc: PrintDoc;
  readonly stationId: string | null;
  readonly firedAt: string | null;
  readonly copyOf: string | null;
  readonly createdAt: string;
  readonly printedAt: string | null;
}

/** One booking, as the office works with it (`/v1/bookings`). */
export interface BookingRow {
  readonly bookingId: string;
  readonly startsAt: string;
  readonly durationMinutes: number;
  readonly party: number;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
  readonly note: string | null;
  readonly source: "ONLINE" | "PHONE" | "WALK_IN";
  readonly status: "BOOKED" | "SEATED" | "NO_SHOW" | "CANCELLED";
  readonly tableRef: string | null;
}

/** How the venue takes table bookings; staff can always take one by phone. */
export interface BookingRules {
  readonly enabled: boolean;
  readonly capacityCovers: number;
  readonly slotMinutes: number;
  readonly durationMinutes: number;
  readonly daysAhead: number;
  readonly minNoticeMinutes: number;
  readonly maxParty: number;
}

const optBig = (value: unknown): bigint | null => (value === null || value === undefined ? null : big(value));
const wireLine = (l: StockLine) => ({ itemRef: l.itemRef, quantityMilli: l.quantityMilli.toString(), unit: l.unit });

export class ConsoleApi {
  readonly #args: ApiArgs;

  constructor(args: ApiArgs) {
    this.#args = args;
  }

  get venueId(): string {
    return this.#args.venueId;
  }

  async #get<T>(path: string): Promise<T> {
    const doFetch = this.#args.fetch ?? fetch;
    let response: Response;
    try {
      response = await doFetch(`${this.#args.baseUrl}${path}`, {
        headers: { authorization: `Bearer ${this.#args.apiKey}` },
      });
    } catch (cause) {
      /**
       * A NETWORK FAILURE IS NOT AN EMPTY REPORT.
       *
       * Returning `[]` here would put a zero on a manager's screen that is
       * indistinguishable from a day the venue took nothing — which is the one
       * number they must be able to trust.
       */
      throw new ApiError(0, "unreachable", (cause as Error).message);
    }

    if (!response.ok) {
      let kind = `http-${String(response.status)}`;
      let detail = response.statusText;
      try {
        const problem = (await response.json()) as { type?: string; detail?: string };
        if (problem.type) kind = problem.type.split("/").pop() ?? kind;
        if (problem.detail) detail = problem.detail;
      } catch {
        // A body that is not a problem document tells us nothing extra; the
        // status already said what happened. Swallowing the parse error here is
        // deliberate and narrow.
        detail = response.statusText;
      }
      throw new ApiError(response.status, kind, detail);
    }

    return (await response.json()) as T;
  }

  /** What sold, who sold it and when, for a range. */
  async breakdown(from: string, to: string): Promise<Breakdown> {
    const wire = await this.#get<{
      categories: Breakdown["categories"];
      items: (Omit<Breakdown["items"][number], "grossMinor" | "discountMinor" | "compMinor" | "chargedMinor"> &
        Record<"grossMinor" | "discountMinor" | "compMinor" | "chargedMinor", string>)[];
      staff: (Omit<Breakdown["staff"][number], "salesMinor" | "averageMinor" | "tipMinor" | "discountMinor" | "compMinor" | "voidGrossMinor"> &
        Record<"salesMinor" | "averageMinor" | "tipMinor" | "discountMinor" | "compMinor" | "voidGrossMinor", string>)[];
      hours: { hour: number; checkCount: number; salesMinor: string }[];
    }>(`/v1/reports/breakdown?venueId=${this.#args.venueId}&from=${from}&to=${to}`);
    const n = (v: string) => BigInt(v);
    return {
      categories: wire.categories,
      items: wire.items.map((i) => ({
        ...i,
        grossMinor: n(i.grossMinor),
        discountMinor: n(i.discountMinor),
        compMinor: n(i.compMinor),
        chargedMinor: n(i.chargedMinor),
      })),
      staff: wire.staff.map((r) => ({
        ...r,
        salesMinor: n(r.salesMinor),
        averageMinor: n(r.averageMinor),
        tipMinor: n(r.tipMinor),
        discountMinor: n(r.discountMinor),
        compMinor: n(r.compMinor),
        voidGrossMinor: n(r.voidGrossMinor),
      })),
      hours: wire.hours.map((h) => ({ ...h, salesMinor: n(h.salesMinor) })),
    };
  }

  async sales(from: string, to: string): Promise<SalesReport> {
    const wire = await this.#get<{
      from: string;
      to: string;
      days: WireDay[];
      totals: WireDay;
      gaps: { businessDate: string; reason: string }[];
    }>(`/v1/reports/sales?venueId=${this.#args.venueId}&from=${from}&to=${to}`);

    return {
      from: wire.from,
      to: wire.to,
      days: wire.days.map(day),
      totals: day(wire.totals),
      gaps: wire.gaps,
    };
  }

  async checks(from: string, to: string): Promise<CheckSummary[]> {
    const wire = await this.#get<{
      checks: (Omit<
        CheckSummary,
        "dueMinor" | "vatMinor" | "tipMinor" | "outstandingMinor" | "tenderKinds"
      > & {
        dueMinor: string;
        vatMinor: string;
        tipMinor: string;
        outstandingMinor: string;
        tenderKinds: string[];
      })[];
    }>(`/v1/checks?venueId=${this.#args.venueId}&from=${from}&to=${to}`);

    return wire.checks.map((c) => ({
      ...c,
      dueMinor: big(c.dueMinor),
      vatMinor: big(c.vatMinor),
      tipMinor: big(c.tipMinor),
      outstandingMinor: big(c.outstandingMinor),
      tenderKinds: c.tenderKinds,
    }));
  }

  async check(orderId: string): Promise<CheckDetail> {
    const wire = await this.#get<Record<string, unknown>>(`/v1/checks/${orderId}`);

    const checks = (wire["checks"] as Record<string, unknown>[]).map((c) => ({
      checkId: String(c["checkId"]),
      closedAt: (c["closedAt"] as string | null) ?? null,
      dueMinor: big(c["dueMinor"]),
      vatMinor: big(c["vatMinor"]),
      netMinor: big(c["netMinor"]),
      tipMinor: big(c["tipMinor"]),
      roundingMinor: big(c["roundingMinor"]),
      taxLines: taxLines(c["taxLines"] as WireTaxLine[]),
      receiptNumber: (c["receiptNumber"] as string | null) ?? null,
      fiscal: (c["fiscal"] as CheckFiscalStatus | null) ?? null,
    }));

    const lines = (wire["lines"] as Record<string, unknown>[]).map((l) => ({
      selectionId: String(l["selectionId"]),
      name: String(l["name"]),
      quantityMilli: Number(l["quantityMilli"]),
      grossMinor: big(l["grossMinor"]),
      rateBp: Number(l["rateBp"]),
      firedAt: (l["firedAt"] as string | null) ?? null,
      voided: Boolean(l["voided"]),
      voidReason: (l["voidReason"] as string | null) ?? null,
    }));

    const tenders = (wire["tenders"] as Record<string, unknown>[]).map((t) => ({
      tenderId: String(t["tenderId"]),
      kind: String(t["kind"]),
      amountMinor: big(t["amountMinor"]),
      tipMinor: big(t["tipMinor"]),
      roundingMinor: big(t["roundingMinor"]),
      voided: Boolean(t["voided"]),
    }));

    return {
      orderId: String(wire["orderId"]),
      businessDate: String(wire["businessDate"]),
      checks,
      lines,
      tenders,
      anomalies: wire["anomalies"] as { kind: string; detail: string }[],
    };
  }

  async shifts(from: string, to: string): Promise<ShiftPage> {
    const wire = await this.#get<{
      shifts: Record<string, unknown>[];
      unattributed: Record<string, unknown>;
    }>(`/v1/shifts?venueId=${this.#args.venueId}&from=${from}&to=${to}`);

    const shifts = wire.shifts.map((s) => {
      const variance = s["variance"] as Record<string, unknown> | null;
      return {
        shiftId: String(s["shiftId"]),
        businessDate: String(s["businessDate"]),
        cashierId: String(s["cashierId"]),
        // Null for somebody off the roster; the screen falls back to the id,
        // because an unnamed cashier still has to be identifiable.
        cashierName: (s["cashierName"] as string | null) ?? null,
        drawerId: String(s["drawerId"]),
        openedAt: String(s["openedAt"]),
        closedAt: (s["closedAt"] as string | null) ?? null,
        state: (s["state"] === "CLOSED" ? "CLOSED" : "OPEN") as "OPEN" | "CLOSED",
        expectedMinor: big(s["expectedMinor"]),
        countedMinor: s["countedMinor"] === null ? null : big(s["countedMinor"]),
        breakdown: Object.fromEntries(
          Object.entries(s["breakdown"] as Record<string, string>).map(([k, v]) => [k, big(v)]),
        ),
        variance:
          variance === null
            ? null
            : {
                varianceMinor: big(variance["varianceMinor"]),
                direction: variance["direction"] as "OVER" | "SHORT" | "EXACT",
                severity: variance["severity"] as "OK" | "WATCH" | "ALERT",
                relativeBp: (variance["relativeBp"] as number | null) ?? null,
              },
        movements: (s["movements"] as Record<string, unknown>[]).map((m) => ({
          kind: String(m["kind"]),
          amountMinor: big(m["amountMinor"]),
          reasonCode: String(m["reasonCode"]),
        })),
        anomalies: s["anomalies"] as { kind: string; detail: string }[],
      };
    });

    const u = wire.unattributed;
    return {
      shifts,
      unattributed: {
        cashMinor: big(u["cashMinor"]),
        tenders: Number(u["tenders"] ?? 0),
        ambiguous: Number(u["ambiguous"] ?? 0),
      },
    };
  }

  async kitchenTimings(from: string, to: string): Promise<KitchenTimings> {
    return this.#get<KitchenTimings>(
      `/v1/kitchen/timings?venueId=${this.#args.venueId}&from=${from}&to=${to}`,
    );
  }

  async fiscal(): Promise<FiscalState> {
    return this.#get<FiscalState>(`/v1/fiscal/exceptions?venueId=${this.#args.venueId}`);
  }

  async numberingGaps(period: string): Promise<{ gaps: Record<string, unknown>[] }> {
    return this.#get(`/v1/numbering/gaps?venueId=${this.#args.venueId}&period=${period}`);
  }

  async numberingRegister(period: string): Promise<{
    assignments: { number: number; documentRef: string; assignedAt: string; late: boolean }[];
  }> {
    return this.#get(`/v1/numbering/register?venueId=${this.#args.venueId}&period=${period}`);
  }

  /**
   * A write, which this client had none of until the menu became editable.
   *
   * Every mutation on this API carries an `Idempotency-Key` — required rather
   * than honoured-if-present, so a retried PATCH cannot apply twice. The key is
   * generated per CALL rather than per edit, because two taps on the same price
   * are two intentional edits and the second must not be swallowed as a retry.
   */
  async #send<T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
    const doFetch = this.#args.fetch ?? fetch;
    let response: Response;
    try {
      response = await doFetch(`${this.#args.baseUrl}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${this.#args.apiKey}`,
          "content-type": "application/json",
          "idempotency-key": crypto.randomUUID(),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (cause) {
      throw new ApiError(0, "unreachable", (cause as Error).message);
    }

    if (!response.ok) {
      let kind = `http-${String(response.status)}`;
      let detail = response.statusText;
      try {
        const problem = (await response.json()) as { type?: string; detail?: string };
        if (problem.type) kind = problem.type.split("/").pop() ?? kind;
        if (problem.detail) detail = problem.detail;
      } catch {
        // A body that is not a problem document tells us nothing the status
        // did not. Narrow and deliberate.
        detail = response.statusText;
      }
      throw new ApiError(response.status, kind, detail);
    }

    // No Content: a PATCH that changed something and has nothing to say.
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }



  /**
   * Take a dish off, or put it back. LIVE, with no publish.
   *
   * The only pair of calls on this client that changes what a till sells the
   * moment they return. A price edit writes to the draft and waits for
   * `publishMenu`; this reaches every till, the kitchen screen and every
   * guest's phone within seconds and mints no version — because "we are out of
   * the sea bass" is not a menu change, and recording it as one would put an
   * immutable version in the history for every Tuesday.
   */
  async snooze(itemRef: string, reason: string): Promise<void> {
    await this.#send("POST", `/v1/menu/snooze?venueId=${this.#args.venueId}`, {
      ref: itemRef,
      reason,
    });
  }

  async unsnooze(itemRef: string): Promise<void> {
    await this.#send(
      "DELETE",
      `/v1/menu/snooze?venueId=${this.#args.venueId}&ref=${encodeURIComponent(itemRef)}`,
    );
  }

  /** What is off right now. Carries no menu version, by design. */
  async availability(): Promise<Record<string, { reason: string; until: string }>> {
    const wire = await this.#get<{
      unavailable?: { ref: string; reason: string; until: string }[];
    }>(`/v1/menu/availability?venueId=${this.#args.venueId}`);

    const out: Record<string, { reason: string; until: string }> = {};
    for (const entry of wire.unavailable ?? []) {
      out[entry.ref] = { reason: entry.reason, until: entry.until };
    }
    return out;
  }

  /**
   * Mint the cards that go on the tables.
   *
   * THE CODES COME FROM THE SERVER and the venue key never reaches this
   * browser. The console is a client of the public API — we dogfood it — so a
   * mint function here would put the venue's whole guest surface into the
   * devtools of a shared office laptop.
   */
  async tableCards(tables: readonly string[]): Promise<TableCards> {
    return this.#get<TableCards>(
      `/v1/table-cards?venueId=${this.#args.venueId}` +
        `&tables=${encodeURIComponent(tables.join(","))}`,
    );
  }

  /** The draft, including everything switched off. */
  async menuDraft(): Promise<DraftMenu> {
    const wire = await this.#get<{
      unpublishedChanges: boolean;
      categories: DraftMenu["categories"];
      stations: DraftMenu["stations"];
      modifierGroups: (Omit<DraftGroup, "options"> & {
        options: (Omit<DraftOption, "priceDeltaMinor"> & { priceDeltaMinor: string })[];
      })[];
      items: (Omit<DraftItem, "grossMinor"> & { grossMinor: string })[];
    }>(`/v1/menu/draft?venueId=${this.#args.venueId}`);
    return {
      unpublishedChanges: wire.unpublishedChanges === true,
      categories: wire.categories,
      stations: wire.stations,
      modifierGroups: wire.modifierGroups.map((g) => ({
        ...g,
        options: g.options.map((o) => ({ ...o, priceDeltaMinor: big(o.priceDeltaMinor) })),
      })),
      items: wire.items.map((i) => ({ ...i, grossMinor: big(i.grossMinor) })),
    };
  }

  async createItem(input: ItemInput): Promise<string> {
    const made = await this.#send<{ itemRef: string }>(
      "POST",
      `/v1/menu/items?venueId=${this.#args.venueId}`,
      itemWire(input),
    );
    return made.itemRef;
  }

  /** Any subset of the item. Nothing a till sees moves until publish. */
  async updateItem(itemRef: string, input: ItemInput): Promise<void> {
    await this.#send(
      "PATCH",
      `/v1/menu/items/${encodeURIComponent(itemRef)}?venueId=${this.#args.venueId}`,
      itemWire(input),
    );
  }

  async createCategory(name: string, sortOrder: number): Promise<string> {
    const made = await this.#send<{ categoryId: string }>(
      "POST",
      `/v1/menu/categories?venueId=${this.#args.venueId}`,
      { name, sortOrder },
    );
    return made.categoryId;
  }

  async updateCategory(
    categoryId: string,
    change: { name?: string; sortOrder?: number; active?: boolean },
  ): Promise<void> {
    await this.#send(
      "PATCH",
      `/v1/menu/categories/${encodeURIComponent(categoryId)}?venueId=${this.#args.venueId}`,
      change,
    );
  }

  /** Create when `groupId` is null, otherwise replace the group and its option list whole. */
  async saveGroup(groupId: string | null, input: GroupInput): Promise<string> {
    const body = {
      ...input,
      options: input.options.map((o) => ({ ...o, priceDeltaMinor: o.priceDeltaMinor.toString() })),
    };
    if (groupId === null) {
      const made = await this.#send<{ groupId: string }>(
        "POST",
        `/v1/menu/modifier-groups?venueId=${this.#args.venueId}`,
        body,
      );
      return made.groupId;
    }
    await this.#send(
      "PUT",
      `/v1/menu/modifier-groups/${encodeURIComponent(groupId)}?venueId=${this.#args.venueId}`,
      body,
    );
    return groupId;
  }

  async saveStation(stationId: string, name: string): Promise<void> {
    await this.#send(
      "PUT",
      `/v1/menu/stations/${encodeURIComponent(stationId)}?venueId=${this.#args.venueId}`,
      { name },
    );
  }

  /**
   * What each tax category costs sitting down at this venue, in basis points,
   * from the registry: the office holds no copy of the law. A category with no
   * rule in force is left out.
   */
  async dineInRates(taxCategories: readonly string[]): Promise<Record<string, number>> {
    const { venues } = await this.venues();
    const venue = venues.find((v) => v.venueId === this.#args.venueId);
    if (!venue) return {};
    const rates: Record<string, number> = {};
    await Promise.all(
      taxCategories.map(async (taxCategory) => {
        const query = new URLSearchParams({
          jurisdiction: venue.jurisdiction,
          taxCategory,
          consumptionMode: "DINE_IN",
          venueType: venue.venueType,
        });
        try {
          rates[taxCategory] = (await this.#get<{ rateBp: number }>(`/v1/tax/rate?${query.toString()}`)).rateBp;
        } catch {
          // No rule: nothing to compare against.
        }
      }),
    );
    return rates;
  }

  /** Rows read from a spreadsheet, into the draft in one go; what was already there is skipped. */
  async importMenu(items: readonly PlannedItem[]): Promise<{ created: number; skipped: readonly string[] }> {
    return this.#send("POST", `/v1/menu/import?venueId=${this.#args.venueId}`, { items });
  }

  /** Freeze a new version. This is the moment an edit reaches a till. */
  async publishMenu(): Promise<{ version: number }> {
    return this.#send("POST", `/v1/menu/publish?venueId=${this.#args.venueId}`);
  }

  async menu(): Promise<Menu> {
    const wire = await this.#get<{
      version: number;
      resolvedAt: string;
      items: {
        itemRef: string;
        name: string;
        stationId: string;
        allergens: string[];
        byMode: Record<string, { grossMinor: string; vatMinor: string; rateBp: number }>;
      }[];
    }>(`/v1/menu?venueId=${this.#args.venueId}`);

    return {
      version: wire.version,
      resolvedAt: wire.resolvedAt,
      items: wire.items.map((i) => ({
        itemRef: i.itemRef,
        name: i.name,
        stationId: i.stationId,
        allergens: i.allergens,
        byMode: Object.fromEntries(
          Object.entries(i.byMode).map(([mode, p]) => [
            mode,
            { grossMinor: big(p.grossMinor), vatMinor: big(p.vatMinor), rateBp: p.rateBp },
          ]),
        ),
      })),
    };
  }

  async staff(): Promise<{ staff: StaffRow[] }> {
    return this.#get(`/v1/org/staff?venueId=${this.#args.venueId}`);
  }

  /** Somebody new, with the PIN they will sign in with. Returns their id. */
  async addStaff(input: { displayName: string; roleId: string; pin: string }): Promise<string> {
    const created = await this.#send<{ staffId: string }>("POST", `/v1/org/staff?venueId=${this.#args.venueId}`, input);
    return created.staffId;
  }

  async updateStaff(staffId: string, change: { displayName?: string; roleId?: string; active?: boolean }): Promise<void> {
    await this.#send("PATCH", `/v1/org/staff/${encodeURIComponent(staffId)}?venueId=${this.#args.venueId}`, change);
  }

  /** The company on the receipt and the floor, as the office edits them. */
  async venueSettings(): Promise<VenueSettings> {
    return this.#get(`/v1/venue/settings?venueId=${this.#args.venueId}`);
  }

  async updateIdentity(patch: Partial<VenueIdentity>): Promise<void> {
    await this.#send("PATCH", `/v1/venue/identity?venueId=${this.#args.venueId}`, patch);
  }

  /** Add a table (null ref) or change one. Returns its ref. */
  async saveTable(
    tableRef: string | null,
    table: { name: string; seats: number; area: string; active?: boolean },
  ): Promise<string> {
    const saved = tableRef
      ? await this.#send<{ tableRef: string }>("PUT", `/v1/venue/floor/${encodeURIComponent(tableRef)}?venueId=${this.#args.venueId}`, table)
      : await this.#send<{ tableRef: string }>("POST", `/v1/venue/floor?venueId=${this.#args.venueId}`, table);
    return saved.tableRef;
  }

  async modules(): Promise<VenueModules> {
    return this.#get(`/v1/venue/modules?venueId=${this.#args.venueId}`);
  }

  /** The owner's choice, whole: a preset or a list. Live on tills in seconds. */
  async saveModules(choice: { preset: string } | { selection: readonly string[] }): Promise<VenueModules> {
    return this.#send("PUT", `/v1/venue/modules?venueId=${this.#args.venueId}`, choice);
  }

  /** Orders from guests' phones, on or off. Live at once; nothing to publish. */
  async setGuestOrdering(enabled: boolean): Promise<void> {
    await this.#send("PUT", `/v1/venue/guest-ordering?venueId=${this.#args.venueId}`, { enabled });
  }

  /** Which Pokladna each payment form is booked into, replaced whole. */
  async savePohoda(settings: PohodaSettings): Promise<void> {
    await this.#send("PUT", `/v1/venue/bookkeeping/pohoda?venueId=${this.#args.venueId}`, settings);
  }

  /**
   * The accountant's file for a range: the bytes as Pohoda imports them
   * (windows-1250), never decoded here. A payment form with no Pokladna comes
   * back as the refusal's message, comma-separated, for the screen to name.
   */
  async pohodaExport(from: string, to: string): Promise<Blob> {
    const doFetch = this.#args.fetch ?? fetch;
    let response: Response;
    try {
      response = await doFetch(
        `${this.#args.baseUrl}/v1/exports/pohoda?venueId=${this.#args.venueId}&from=${from}&to=${to}`,
        { headers: { authorization: `Bearer ${this.#args.apiKey}` } },
      );
    } catch (cause) {
      throw new ApiError(0, "unreachable", (cause as Error).message);
    }
    if (!response.ok) {
      const problem = (await response.json().catch(() => ({}))) as { type?: string; detail?: string; kinds?: string[] };
      throw new ApiError(
        response.status,
        problem.type?.split("/").pop() ?? `http-${String(response.status)}`,
        problem.kinds?.join(",") ?? problem.detail ?? response.statusText,
      );
    }
    return response.blob();
  }

  /** Re-sign the bundle from the venue's rows; every till fetches it at once. */
  async publishVenue(): Promise<{ version: number }> {
    return this.#send("POST", `/v1/venue/publish?venueId=${this.#args.venueId}`);
  }

  /** A one-time code a new device types to become this venue's till or kitchen screen. */
  async pairDevice(input: {
    name: string;
    kind: "till" | "kitchen";
    stationId?: string;
    serviceMode?: "TABLE" | "COUNTER";
  }): Promise<{
    code: string;
    expiresAt: string;
    deviceId: string;
  }> {
    return this.#send("POST", `/v1/devices/pairing?venueId=${this.#args.venueId}`, input);
  }

  /** Revoke every key the device holds; it stops at its next request. */
  async unpairDevice(deviceId: string): Promise<void> {
    await this.#send("DELETE", `/v1/devices/${encodeURIComponent(deviceId)}?venueId=${this.#args.venueId}`);
  }

  /** A new PIN. Every till forgets the old one at its next roster. */
  async resetPin(staffId: string, pin: string): Promise<void> {
    await this.#send("PUT", `/v1/org/staff/${encodeURIComponent(staffId)}/pin?venueId=${this.#args.venueId}`, { pin });
  }

  async venues(): Promise<{
    venues: {
      venueId: string;
      name: string;
      venueType: string;
      jurisdiction: string;
      timezone: string;
      businessDayStartMinutes: number;
      fiscalRegistrationId: string | null;
    }[];
  }> {
    return this.#get("/v1/org/venues");
  }

  async devices(): Promise<{
    devices: {
      deviceId: string;
      name: string;
      fiscalTillId: string | null;
      serviceMode?: "TABLE" | "COUNTER";
      pickupPrefix?: string | null;
    }[];
  }> {
    return this.#get(`/v1/org/devices?venueId=${this.#args.venueId}`);
  }

  /** A till at the tables or at the counter; a counter till gets its letter. */
  async setDeviceService(deviceId: string, serviceMode: "TABLE" | "COUNTER"): Promise<{ serviceMode: string; pickupPrefix: string | null }> {
    return this.#send("PUT", `/v1/devices/${encodeURIComponent(deviceId)}/service?venueId=${this.#args.venueId}`, { serviceMode });
  }

  async firedNotBilled(from: string, to: string): Promise<{ lines: Record<string, unknown>[] }> {
    return this.#get(
      `/v1/reports/fired-not-billed?venueId=${this.#args.venueId}&from=${from}&to=${to}`,
    );
  }

  /**
   * A dish's photo, already shrunk in the browser (`photo.ts`). The server
   * reads its format from its bytes and answers with its sha-256.
   */
  async uploadPhoto(photo: Blob): Promise<{ sha: string }> {
    const doFetch = this.#args.fetch ?? fetch;
    let response: Response;
    try {
      response = await doFetch(`${this.#args.baseUrl}/v1/menu/photos?venueId=${this.#args.venueId}`, {
        method: "POST",
        headers: { authorization: `Bearer ${this.#args.apiKey}`, "content-type": photo.type || "image/webp", "idempotency-key": crypto.randomUUID() },
        body: photo,
      });
    } catch (cause) {
      throw new ApiError(0, "unreachable", (cause as Error).message);
    }
    if (!response.ok) {
      const problem = (await response.json().catch(() => ({}))) as { type?: string; detail?: string };
      throw new ApiError(response.status, problem.type?.split("/").pop() ?? `http-${response.status}`, problem.detail ?? response.statusText);
    }
    return (await response.json()) as { sha: string };
  }

  /** Where a photo is shown from — the same immutable URL a guest's phone uses. */
  photoUrl(sha: string): string {
    return `${this.#args.baseUrl}/public/v1/photos/${this.#args.venueId}/${sha}`;
  }

  async publicPage(): Promise<PublicPage> {
    return this.#get(`/v1/venue/public?venueId=${this.#args.venueId}`);
  }

  /** The address, the page, or both; `slug: null` takes the page down. */
  async savePublicPage(change: { slug?: string | null; profile?: PublicPage["profile"] }): Promise<PublicPage> {
    return this.#send("PUT", `/v1/venue/public?venueId=${this.#args.venueId}`, change);
  }

  /** Who changed what between two instants, newest first, and whether the record is intact. */
  /** The venue's printers, and what a printer may be. */
  async printers(): Promise<Printers> {
    return this.#get(`/v1/print/printers?venueId=${this.#args.venueId}`);
  }

  /** A roll, newest first; without a printer, every job — the unrouted among them. */
  async printJobs(from: string, to: string, printerId?: string): Promise<{ jobs: PrintJobRow[] }> {
    const which = printerId ? `&printerId=${encodeURIComponent(printerId)}` : "";
    return this.#get(`/v1/print/jobs?venueId=${this.#args.venueId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${which}`);
  }

  async savePrinter(printerId: string, input: Omit<PrinterRow, "printerId" | "activeSince">): Promise<PrinterRow> {
    return this.#send("PUT", `/v1/print/printers/${encodeURIComponent(printerId)}?venueId=${this.#args.venueId}`, input);
  }

  /** A copy at the same printer, saying KOPIE. */
  async reprint(jobId: string): Promise<{ job: PrintJobRow }> {
    return this.#send("POST", `/v1/print/jobs/${encodeURIComponent(jobId)}/reprint?venueId=${this.#args.venueId}`, {});
  }

  async auditLog(from: string, to: string): Promise<AuditLog> {
    return this.#get(`/v1/audit?venueId=${this.#args.venueId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  }

  /** A day's bookings by the venue's calendar, with how many it seats at once. */
  async bookings(date?: string): Promise<{ date: string; capacityCovers: number; slotMinutes: number; bookings: BookingRow[] }> {
    // No date: the venue's own trading day, which only the server knows for certain.
    return this.#get(`/v1/bookings?venueId=${this.#args.venueId}${date ? `&date=${encodeURIComponent(date)}` : ""}`);
  }

  /** A booking by phone; `force` squeezes one in past capacity, knowingly. */
  async addBooking(input: { startsAt: string; party: number; name: string; phone?: string; note?: string; force?: boolean }): Promise<BookingRow> {
    return this.#send("POST", `/v1/bookings?venueId=${this.#args.venueId}`, input);
  }

  /** A table, seated, a no-show, cancelled, a different count. */
  async changeBooking(
    bookingId: string,
    change: { status?: BookingRow["status"]; tableRef?: string | null; party?: number },
  ): Promise<BookingRow> {
    return this.#send("PATCH", `/v1/bookings/${encodeURIComponent(bookingId)}?venueId=${this.#args.venueId}`, change);
  }

  /** What was approved by somebody without the authority to, in a range of instants. */
  async approvalFlags(from: string, to: string): Promise<ApprovalFlagRow[]> {
    const wire = await this.#get<{ flags: ApprovalFlagRow[] }>(
      `/v1/approvals/flags?venueId=${this.#args.venueId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
    return wire.flags;
  }

  // --- Stock ------------------------------------------------------------------------

  async stockItems(): Promise<StockItemRow[]> {
    const wire = await this.#get<{ items: Record<string, unknown>[] }>(`/v1/stock/items?venueId=${this.#args.venueId}`);
    return wire.items.map((i) => ({
      itemRef: String(i["itemRef"]),
      name: String(i["name"]),
      unit: i["unit"] as StockUnit,
      category: String(i["category"] ?? ""),
      active: i["active"] !== false,
      reorderMilli: optBig(i["reorderMilli"]),
      standardCostMilliMinor: optBig(i["standardCostMilliMinor"]),
      onHandMilli: big(i["onHandMilli"]),
      valueMinor: big(i["valueMinor"]),
      unitCostMilliMinor: optBig(i["unitCostMilliMinor"]),
      low: i["low"] === true,
    }));
  }

  /** What is running low, for the badge on Dnes. */
  async stockLow(): Promise<{ itemRef: string; name: string; unit: StockUnit; onHandMilli: bigint; reorderMilli: bigint }[]> {
    const wire = await this.#get<{ low: { itemRef: string; name: string; unit: StockUnit; onHandMilli: string; reorderMilli: string }[] }>(
      `/v1/stock/low?venueId=${this.#args.venueId}`,
    );
    return wire.low.map((l) => ({ ...l, onHandMilli: big(l.onHandMilli), reorderMilli: big(l.reorderMilli) }));
  }

  async saveStockItem(itemRef: string, item: StockItemInput): Promise<void> {
    await this.#send("PUT", `/v1/stock/items/${encodeURIComponent(itemRef)}?venueId=${this.#args.venueId}`, {
      name: item.name,
      unit: item.unit,
      category: item.category,
      reorderMilli: item.reorderMilli?.toString() ?? null,
      standardCostMilliMinor: item.standardCostMilliMinor?.toString() ?? null,
      active: item.active,
    });
  }

  async suppliers(): Promise<SupplierRow[]> {
    return (await this.#get<{ suppliers: SupplierRow[] }>(`/v1/stock/suppliers?venueId=${this.#args.venueId}`)).suppliers;
  }

  async saveSupplier(supplier: SupplierRow): Promise<void> {
    await this.#send("PUT", `/v1/stock/suppliers/${encodeURIComponent(supplier.supplierRef)}?venueId=${this.#args.venueId}`, {
      name: supplier.name,
      registrationId: supplier.registrationId,
    });
  }

  async recipes(): Promise<{ recipes: RecipeRow[]; optionDeltas: OptionDeltaRow[] }> {
    const wire = await this.#get<{ recipes: Record<string, unknown>[]; optionDeltas: Record<string, unknown>[] }>(
      `/v1/stock/recipes?venueId=${this.#args.venueId}`,
    );
    return {
      recipes: wire.recipes.map((r) => ({
        recipeRef: String(r["recipeRef"]),
        version: Number(r["version"]),
        effectiveFrom: String(r["effectiveFrom"]),
        yieldMilli: big(r["yieldMilli"]),
        yieldUnit: String(r["yieldUnit"]),
        lines: (r["lines"] as Record<string, unknown>[]).map((l) => ({
          componentRef: String(l["componentRef"]),
          quantityMilli: big(l["quantityMilli"]),
          unit: String(l["unit"]),
          ...(typeof l["yieldPctBp"] === "number" ? { yieldPctBp: l["yieldPctBp"] } : {}),
        })),
        costMinor: optBig(r["costMinor"]),
        uncosted: (r["uncosted"] as string[]) ?? [],
      })),
      optionDeltas: wire.optionDeltas.map((d) => ({
        groupId: String(d["groupId"]),
        optionId: String(d["optionId"]),
        version: Number(d["version"]),
        lines: (d["lines"] as { itemRef: string; quantityMilli: string }[]).map((l) => ({ itemRef: l.itemRef, quantityMilli: big(l.quantityMilli) })),
      })),
    };
  }

  /** A new version of a dish's recipe, in force from now. */
  async addRecipe(recipeRef: string, lines: readonly RecipeLineRow[]): Promise<{ version: number }> {
    return this.#send("POST", `/v1/stock/recipes?venueId=${this.#args.venueId}`, {
      recipeRef,
      lines: lines.map((l) => ({
        componentRef: l.componentRef,
        quantityMilli: l.quantityMilli.toString(),
        unit: l.unit,
        ...(l.yieldPctBp !== undefined ? { yieldPctBp: l.yieldPctBp } : {}),
      })),
    });
  }

  /** What a guest's choice changes in stock, per one taken. */
  async addOptionDelta(groupId: string, optionId: string, lines: readonly { itemRef: string; quantityMilli: bigint }[]): Promise<void> {
    await this.#send("POST", `/v1/stock/option-deltas?venueId=${this.#args.venueId}`, {
      groupId,
      optionId,
      lines: lines.map((l) => ({ itemRef: l.itemRef, quantityMilli: l.quantityMilli.toString() })),
    });
  }

  async stockMovements(filter: { itemRef?: string; kinds?: readonly string[] } = {}): Promise<StockMovementRow[]> {
    const item = filter.itemRef ? `&itemRef=${encodeURIComponent(filter.itemRef)}` : "";
    const kind = filter.kinds && filter.kinds.length > 0 ? `&kind=${filter.kinds.join(",")}` : "";
    const wire = await this.#get<{ movements: Record<string, unknown>[] }>(`/v1/stock/movements?venueId=${this.#args.venueId}${item}${kind}`);
    return wire.movements.map((m) => ({
      entryId: String(m["entryId"]),
      itemRef: String(m["itemRef"]),
      kind: String(m["kind"]),
      quantityMilli: big(m["quantityMilli"]),
      valueMinor: big(m["valueMinor"]),
      occurredAt: String(m["occurredAt"]),
      sourceRef: String(m["sourceRef"]),
      reasonCode: (m["reasonCode"] as string | null) ?? null,
    }));
  }

  /** Food cost over a range of instants, mapped sales only. */
  async foodCost(from: string, to: string): Promise<{ netMinor: bigint; costMinor: bigint; dishes: FoodCostRow[] }> {
    const wire = await this.#get<{ netMinor: string; costMinor: string; dishes: Record<string, string>[] }>(
      `/v1/stock/food-cost?venueId=${this.#args.venueId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
    return {
      netMinor: big(wire.netMinor),
      costMinor: big(wire.costMinor),
      dishes: wire.dishes.map((d) => ({
        itemRef: String(d["itemRef"]),
        soldMilli: big(d["soldMilli"]),
        netMinor: big(d["netMinor"]),
        costMinor: big(d["costMinor"]),
      })),
    };
  }

  async stockUnmapped(): Promise<{
    unmapped: { itemRef: string; lines: number; soldMilli: bigint; lastSoldAt: string }[];
    exceptions: { sourceRef: string; kind: string; detail: string; occurredAt: string }[];
  }> {
    const wire = await this.#get<{
      unmapped: { itemRef: string; lines: number; soldMilli: string; lastSoldAt: string }[];
      exceptions: { sourceRef: string; kind: string; detail: string; occurredAt: string }[];
    }>(`/v1/stock/unmapped?venueId=${this.#args.venueId}`);
    return { unmapped: wire.unmapped.map((u) => ({ ...u, soldMilli: big(u.soldMilli) })), exceptions: wire.exceptions };
  }

  async receiveStock(delivery: {
    supplierRef?: string;
    documentNumber?: string;
    lines: readonly (StockLine & { netMinor: bigint })[];
  }): Promise<{ applied: boolean }> {
    return this.#send("POST", `/v1/stock/receipts?venueId=${this.#args.venueId}`, {
      ...(delivery.supplierRef ? { supplierRef: delivery.supplierRef } : {}),
      ...(delivery.documentNumber ? { documentNumber: delivery.documentNumber } : {}),
      lines: delivery.lines.map((l) => ({ ...wireLine(l), netMinor: l.netMinor.toString() })),
    });
  }

  async wasteStock(reasonCode: string, lines: readonly StockLine[]): Promise<{ applied: boolean }> {
    return this.#send("POST", `/v1/stock/waste?venueId=${this.#args.venueId}`, { reasonCode, lines: lines.map(wireLine) });
  }

  /** What a count would adjust, valued — nothing recorded. */
  async previewCount(lines: readonly StockLine[]): Promise<CountPreviewLine[]> {
    const wire = await this.#send<{ lines: Record<string, string | null>[] }>(
      "POST",
      `/v1/stock/counts?venueId=${this.#args.venueId}&preview=1`,
      { lines: lines.map(wireLine) },
    );
    return wire.lines.map((l) => ({
      itemRef: String(l["itemRef"]),
      expectedMilli: big(l["expectedMilli"]),
      countedMilli: big(l["countedMilli"]),
      varianceMilli: big(l["varianceMilli"]),
      varianceMinor: optBig(l["varianceMinor"]),
    }));
  }

  async postCount(lines: readonly StockLine[]): Promise<{ applied: boolean }> {
    return this.#send("POST", `/v1/stock/counts?venueId=${this.#args.venueId}`, { lines: lines.map(wireLine) });
  }
}
